import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/leuteliaApi";
import { isDemo } from "@/lib/demo";
import { Button } from "@/components/ui/button";
import { BrainCircuit, Loader2, RefreshCw, Sparkles, Eye, ArrowRight } from "lucide-react";

const POLL_MS = 3000;

export default function Narrativa({ ccu, scope }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await api.narrative(scope.tenant, scope.hotel);
      setData(d);
      return d;
    } catch { return null; }
  }, [scope.tenant, scope.hotel]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const d = await load();
      if (alive && d?.narrative?.status === "pending") timer.current = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { alive = false; clearTimeout(timer.current); };
  }, [load, ccu.understanding_version]);

  const generate = async (force) => {
    setBusy(true);
    try {
      await api.generateNarrative({ tenant: scope.tenant, hotel: scope.hotel, force });
      toast.message("LEUTELIA sta scrivendo la lettura strategica…");
      const tick = async () => {
        const d = await load();
        if (d?.narrative?.status === "pending") timer.current = setTimeout(tick, POLL_MS);
        else setBusy(false);
      };
      tick();
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  const n = data?.narrative;
  const pending = n?.status === "pending" || busy;

  return (
    <section className="mb-8" data-testid="narrative-section">
      <div className="rounded-2xl bg-brand-green text-brand-cream relative overflow-hidden">
        <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-brand-gold/10" />
        <div className="absolute right-24 -bottom-32 w-64 h-64 rounded-full bg-brand-gold/5" />
        <div className="relative p-7">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-brand-gold/15 border border-brand-gold/40 flex items-center justify-center shrink-0">
                <BrainCircuit size={22} className="text-brand-gold" strokeWidth={1.75} />
              </div>
              <div>
                <div className="text-brand-gold font-bold tracking-[0.22em] text-[11px] uppercase">Lettura di LEUTELIA</div>
                <h3 className="font-serif text-[24px] leading-tight mt-1">Lettura strategica</h3>
                <p className="text-[12.5px] text-brand-cream/70 mt-1 max-w-xl">
                  Scritta per te quando cambia qualcosa di importante nella comprensione dell'hotel, oppure quando la richiedi.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {n?.status === "ready" && (
                <span className="text-[11px] font-mono px-2.5 py-1 rounded-full border border-brand-gold/40 text-brand-gold" data-testid="narrative-meta">
                  versione {n.understanding_version}{data?.stale ? " · da aggiornare" : ""}
                </span>
              )}
              {!(isDemo() && n) && <Button data-testid="narrative-generate-btn" disabled={pending} onClick={() => generate(!!n)}
                className="h-9 rounded-lg bg-brand-gold hover:bg-brand-goldSoft text-brand-greenDark text-[13px] font-semibold">
                {pending ? <Loader2 size={15} className="mr-2 animate-spin" /> : n ? <RefreshCw size={15} className="mr-2" /> : <Sparkles size={15} className="mr-2" />}
                {pending ? "In scrittura…" : n ? "Rigenera" : "Genera lettura"}
              </Button>}
            </div>
          </div>

          {!n && !pending && (
            <p className="mt-6 text-[14px] text-brand-cream/80 italic" data-testid="narrative-empty">
              Nessuna lettura ancora disponibile per questa versione. Arriverà al prossimo cambiamento importante, oppure puoi richiederla subito.
            </p>
          )}
          {pending && !n?.headline && (
            <div className="mt-6 space-y-2" data-testid="narrative-pending">
              {[92, 100, 78].map((w, i) => <div key={i} className="h-3 rounded bg-brand-cream/10 animate-pulse" style={{ width: `${w}%` }} />)}
            </div>
          )}
          {n?.status === "failed" && (
            <p className="mt-6 text-[13px] text-red-200" data-testid="narrative-failed">Generazione non riuscita: {n.error}. Riprova.</p>
          )}
          {n?.status === "ready" && (
            <div className="mt-6 grid grid-cols-1 xl:grid-cols-[1.35fr_1fr] gap-7">
              <div>
                <h4 className="font-serif text-[28px] leading-[1.15] text-brand-cream" data-testid="narrative-headline">{n.headline}</h4>
                <div className="mt-4 space-y-3 text-[14.5px] leading-relaxed text-brand-cream/85">
                  {n.narrative.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                {n.watch_out && (
                  <div className="mt-5 flex items-start gap-2 text-[13px] text-brand-goldSoft">
                    <Eye size={16} className="mt-0.5 shrink-0" /> <span>{n.watch_out}</span>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="text-brand-gold font-bold tracking-[0.18em] text-[10.5px] uppercase">Priorità suggerite</div>
                {n.priorities.map((p, i) => (
                  <div key={i} className="rounded-xl bg-brand-cream/[0.06] border border-brand-gold/20 p-4" data-testid={`narrative-priority-${i + 1}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-brand-gold text-brand-greenDark text-[12px] font-bold flex items-center justify-center">{i + 1}</span>
                      <div className="font-serif text-[17px] text-brand-cream leading-snug">{p.title}</div>
                    </div>
                    <p className="text-[12.5px] text-brand-cream/70 mt-2 leading-relaxed">{p.why}</p>
                    <div className="flex items-start gap-1.5 mt-2 text-[12.5px] text-brand-goldSoft">
                      <ArrowRight size={14} className="mt-0.5 shrink-0" /> <span>{p.first_step}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
